import { useState, useEffect } from 'react';
import { useCRMAuth } from '../../context/CRMAuthContext';
import axios from 'axios';
import { Activity, Target, Briefcase, UserCircle, Clock, ArrowRight, Filter } from 'lucide-react';
import CRMLayout from '../../components/crm/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const actionColors = {
  created: 'bg-green-500/10 text-green-400 border-green-500/20',
  updated: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  deleted: 'bg-red-500/10 text-red-400 border-red-500/20',
  moved: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  note_added: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  status_changed: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  assigned: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
};

const entityIcons = { lead: Target, deal: Briefcase, contact: UserCircle, user: UserCircle };

const CRMActivities = () => {
  const { token } = useCRMAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => { fetchActivities(); }, [filter]);

  const fetchActivities = async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.append('entity_type', filter);
      const response = await axios.get(`${API}/api/crm/activities?${params}&limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      setActivities(response.data.activities);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <CRMLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Activity Timeline</h1>
            <p className="text-slate-400">Track all changes and updates</p>
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white">
            <option value="">All Activities</option>
            <option value="lead">Leads</option>
            <option value="deal">Deals</option>
            <option value="contact">Contacts</option>
            <option value="user">Users</option>
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div></div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16 bg-slate-800/50 border border-slate-700/50 rounded-xl">
            <Activity className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No activity yet</h3>
            <p className="text-slate-400">Activity will appear here as you use the CRM</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-700/50"></div>
            
            <div className="space-y-4">
              {activities.map((activity, idx) => {
                const EntityIcon = entityIcons[activity.entity_type] || Activity;
                return (
                  <div key={activity.activity_id || idx} className="relative pl-14">
                    {/* Timeline dot */}
                    <div className="absolute left-4 top-4 w-4 h-4 rounded-full bg-slate-800 border-2 border-blue-500"></div>
                    
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:border-slate-600 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="p-2 bg-slate-700/50 rounded-lg">
                            <EntityIcon className="w-4 h-4 text-slate-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white">{activity.description}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${actionColors[activity.action] || actionColors.updated}`}>
                                {activity.action?.replace('_', ' ')}
                              </span>
                              <span className="text-xs text-slate-500 capitalize">{activity.entity_type}</span>
                              {activity.user_name && <span className="text-xs text-slate-400">by {activity.user_name}</span>}
                            </div>
                            {activity.old_value && activity.new_value && (
                              <div className="flex items-center gap-2 mt-2 text-sm">
                                <span className="text-red-400/70 line-through">{activity.old_value}</span>
                                <ArrowRight className="w-3 h-3 text-slate-500" />
                                <span className="text-green-400">{activity.new_value}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                          <Clock className="w-3 h-3" />
                          {formatDate(activity.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </CRMLayout>
  );
};

export default CRMActivities;
